import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';

import { createShoutOut } from '@/api/comms/shoutOuts.api';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { MemberPicker } from '@/components/comms/MemberPicker';
import { describeCommsError } from '@/lib/comms/errors';

/**
 * "Give a shout-out" composer — recipient picker + a core-value chip
 * (from the shared V/TO query, passed in by the page) + a short message.
 *
 * When the org has no core values yet the form is replaced by a prompt
 * linking to the V/TO — recognition is meaningless without the Bar.
 */
export function ShoutOutComposerModal({
  open,
  onClose,
  coreValues,
  currentMembershipId,
  onCreated,
  initialRecipientId = null,
}: {
  open: boolean;
  onClose: () => void;
  coreValues: string[];
  currentMembershipId: string;
  onCreated: () => void;
  /** Pre-selected recipient — a deep link (`?compose=shoutout&to=…`, e.g. a
   *  streak nudge from ADR-048) lands with the person already picked. */
  initialRecipientId?: string | null;
}) {
  const [recipientId, setRecipientId] = useState<string | null>(null);
  const [coreValue, setCoreValue] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (open && initialRecipientId) setRecipientId(initialRecipientId);
  }, [open, initialRecipientId]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!recipientId || !coreValue) return Promise.reject(new Error('Incomplete shout-out'));
      return createShoutOut({ recipientMembershipId: recipientId, coreValue, message: message.trim() });
    },
    onSuccess: () => {
      onCreated();
      handleClose();
    },
  });

  function handleClose(): void {
    setRecipientId(null);
    setCoreValue(null);
    setMessage('');
    mutation.reset();
    onClose();
  }

  const canSubmit = recipientId !== null && coreValue !== null && message.trim().length > 0;

  return (
    <Modal open={open} onClose={handleClose} title="Give a shout-out" size="md">
      {coreValues.length === 0 ? (
        <div className="space-y-3 py-2 text-center">
          <p className="text-[14px] font-medium text-kw-shark">Define your core values first</p>
          <p className="text-[13px] text-kw-muted">
            Shout-outs are tagged to a core value. Add them on the V/TO to start recognizing teammates.
          </p>
          <Link
            to="/eos/vto"
            onClick={handleClose}
            className="inline-block text-[13px] font-medium text-kw-cerulean hover:underline"
          >
            Open the V/TO
          </Link>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) mutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-kw-label">Recipient</span>
            <MemberPicker
              exclude={[currentMembershipId]}
              onSelect={(ids) => {
                setRecipientId(ids[0] ?? null);
              }}
            />
          </div>

          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-kw-label">Core value</span>
            <div className="flex flex-wrap gap-2">
              {coreValues.map((value) => {
                const active = coreValue === value;
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      setCoreValue(active ? null : value);
                    }}
                    className={`rounded-kw-full border px-3 py-1 text-[13px] transition-colors ${
                      active
                        ? 'border-kw-cerulean bg-kw-cerulean-light text-kw-cerulean-dark'
                        : 'border-kw-border bg-white text-kw-muted hover:bg-kw-bg'
                    }`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Message</span>
            <textarea
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
              }}
              rows={3}
              maxLength={1000}
              placeholder="What did they do that lived this value?"
              className="rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            />
          </label>

          {mutation.error ? (
            <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
              <p className="text-[12px] text-kw-danger-text">
                {describeCommsError(mutation.error, 'Could not post the shout-out.')}
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={mutation.isPending} disabled={!canSubmit}>
              Give shout-out
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
